import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import { Container, Row } from "reactstrap";

// RangeSlider
import "nouislider/distribute/nouislider.css";
import ConfirmDeleteModal from "../../Components/Common/ConfirmDeleteModal";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import TableContainer from "../../Components/Common/TableContainer";

import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  useUserCategories,
  useDeleteUserCategory,
} from "../../hooks/useUserCategories";
import CategoriesOverview from "./CategoriesOverview";

const UsersCategories = (props) => {
  const categoriesQ = useUserCategories();
  const categories = categoriesQ.data || [];
  const isLoading = categoriesQ.isLoading;
  const isError = categoriesQ.isError;
  const errorMessage =
    typeof categoriesQ.error === "string"
      ? categoriesQ.error
      : categoriesQ.error?.message || props.t("Une erreur est survenue");

  const deleteMutation = useDeleteUserCategory();

  const [category, setCategory] = useState(null);

  //delete order
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);

  const onClickDelete = (category) => {
    setCategory(category);
    setDeleteModal(true);
  };

  const handleDeleteCategory = () => {
    if (category) {
      deleteMutation.mutate(category.id, {
        onSuccess: () => setDeleteModal(false),
      });
    }
  };

  // Deleting a category cascades to its institutions, and is refused outright
  // when users are still attached — spelled out in the confirmation modal.
  const deleteImplications = useMemo(() => {
    if (!category) return [];

    const implications = [];
    const subcategoriesCount = category.subcategories_count || 0;

    if (subcategoriesCount > 0) {
      implications.push(
        `la suppression de ${subcategoriesCount} institution${
          subcategoriesCount > 1 ? "s" : ""
        } rattachée${subcategoriesCount > 1 ? "s" : ""} à cette catégorie`
      );
    }
    implications.push("la perte du classement institutionnel correspondant");

    return implications;
  }, [category]);

  const deleteBlockedReason = useMemo(() => {
    const usersCount = category?.users_count || 0;
    if (usersCount === 0) return null;

    return `${usersCount} utilisateur${usersCount > 1 ? "s sont rattachés" : " est rattaché"} à cette catégorie. Rattachez-${
      usersCount > 1 ? "les" : "le"
    } à une autre catégorie avant de la supprimer.`;
  }, [category]);

  // Delete Multiple
  const deleteMultiple = () => {
    const ele = document.querySelectorAll(".productCheckBox:checked");
    const del = document.getElementById("selection-element");
    ele.forEach((element) => {
      deleteMutation.mutate(element.value);
      del.style.display = "none";
    });
  };
  const checkedAll = () => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".entrepriseCheckBox");

    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            onClick={() => checkedAll()}
          />
        ),
        Cell: (cellProps) => {
          return (
            <input
              type="checkbox"
              className="entrepriseCheckBox"
              value={cellProps.row.original.id}
            />
          );
        },
        id: "#",
      },
      {
        Header: props.t("CATEGORIE"),
        accessor: "name",
        Cell: (category) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <p className="fs-14 mb-1">{category.row.original.name}</p>
              </div>
            </div>
          </>
        ),
      },
      {
        Header: props.t("Type d'utilisateur"),
        accessor: "user_type",
        Cell: (category) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <p className="fs-14 mb-1">
                  {category.row.original.user_type?.label ||
                    category.row.original.user_type?.code ||
                    "-"}
                </p>
              </div>
            </div>
          </>
        ),
      },
      {
        Header: props.t("INSTITUTIONS"),
        accessor: "subcategories_count",
        Cell: (category) => (
          <span className="badge bg-info-subtle text-info fs-12">
            {category.row.original.subcategories_count || 0}
          </span>
        ),
      },
      {
        Header: props.t("UTILISATEURS"),
        accessor: "users_count",
        Cell: (category) => (
          <span className="badge bg-primary-subtle text-primary fs-12">
            {category.row.original.users_count || 0}
          </span>
        ),
      },
      {
        Header: "ACTIONS",
        Cell: (cellProps) => {
          const categoriesData = cellProps.row.original;
          return (
            <div>
              <li className="list-inline-item" title={props.t("Modifier")}>
                <Link
                  to={`/utilisateurs-categories-update/${categoriesData.id}/`}
                  className="text-warning d-inline-block"
                >
                  <i className="ri-pencil-fill align-bottom" />
                </Link>
              </li>
              <Link
                to="#"
                className="text-danger d-inline-block"
                onClick={() => onClickDelete(categoriesData)}
              >
                <i className="ri-delete-bin-5-line me-2 align-middle text-danger"></i>
              </Link>
            </div>
          );
        },
      },
    ],
    [props],
  );

  document.title = props.t("Catégories d'institutions");

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />

      <ConfirmDeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteCategory}
        onCloseClick={() => setDeleteModal(false)}
        title={props.t("Supprimer cette catégorie d'institutions ?")}
        itemLabel={category?.name}
        implications={deleteImplications}
        blockedReason={deleteBlockedReason}
        isPending={deleteMutation.isPending}
      />
      <ConfirmDeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
        title={props.t("Supprimer les catégories sélectionnées ?")}
        implications={[
          props.t(
            "la suppression des institutions rattachées à ces catégories"
          ),
        ]}
      />
      <Container fluid>
        <BreadCrumb
          title={props.t("Catégories d'institutions")}
          pageTitle={props.t("Utilisateurs")}
        />

        {/* Statistics for this screen, derived from the list it already loaded:
            no extra request, and the totals can never disagree with the table
            below them. */}
        {!isLoading && !isError ? (
          <CategoriesOverview categories={categories} />
        ) : null}

        <Row>
          <div className="col-xl-12 col-lg-12">
            <div>
              <div className="card">
                <div className="card-body pt-0">
                  <div className="alert alert-info mt-3 mb-0" role="alert">
                    <i className="ri-information-line align-bottom me-1"></i>
                    Une <strong>catégorie d&apos;institutions</strong> regroupe
                    un bloc institutionnel (ex. «&nbsp;Entreprise
                    étatique&nbsp;»). Chaque catégorie contient des{" "}
                    <strong>institutions</strong> qui la précisent (ex.
                    «&nbsp;Ministère de la Justice&nbsp;»). À ne pas confondre
                    avec les catégories d&apos;articles ou de produits.
                  </div>
                  {isLoading ? (
                    <div className="py-4 text-center">
                      <div>
                        <lord-icon
                          src="https://cdn.lordicon.com/xjovhxra.json"
                          trigger="loop"
                          colors="primary:#0960b6,secondary:#02376b"
                          style={{ width: "72px", height: "72px" }}
                        ></lord-icon>
                      </div>
                      <div className="mt-4">
                        <h5>{props.t("Chargement")}...</h5>
                      </div>
                    </div>
                  ) : isError ? (
                    <div className="py-4 text-center">
                      <div className="mt-4">
                        <h5>{errorMessage}</h5>
                      </div>
                    </div>
                  ) : categories && categories.length >= 0 ? (
                    <TableContainer
                      columns={columns}
                      data={categories || []}
                      isGlobalFilter={true}
                      isAddUserCategorie={true}
                      customPageSize={10}
                      divClass="table-responsive mb-1"
                      tableClass="mb-0 align-middle "
                      theadClass="table-light text-muted"
                    />
                  ) : (
                    <div className="py-4 text-center">
                      <div className="mt-4">
                        <h5> {props.t("Désolé, pas de resultats trouvés")} </h5>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

UsersCategories.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(UsersCategories));
